import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import ReactCodeInput from 'react-verification-code-input'
import { formatPhoneNumber } from 'react-phone-number-input'
import classnames from 'classnames'

import { SIGNUP_STEPS } from 'constants/auth'
import { analytics, apiManager } from 'helpers'
import { useLogin } from 'hooks'
import useVerifyPhoneNumber from '../useVerifyPhoneNumber'

import AnimatedCheckIcon from 'components/AnimatedCheckIcon'
import InputErrorMessage from 'components/InputErrorMessage'
import Description from '../components/Description'

import Action from '../components/Action'
import CloseButton from 'components/AuthModal/components/CloseButton'
import Form from 'components/AuthModal/components/Form'
import Label from 'components/AuthModal/components/Label'
import ProgressBar from 'components/AuthModal/components/ProgressBar'

const VERIFICATION_DELAY = 1500

const progressPercent = (1 / (1 + Object.keys(SIGNUP_STEPS).length)) * 100

const VerificationModalCodeValidation = ({ onSubmit, onClose, autoFocus, user }) => {
  const { verify } = useVerifyPhoneNumber()
  const [errorMessage, setErrorMessage] = useState()
  const [loading, setLoading] = useState(false)
  const [verified, setVerified] = useState(false)
  const login = useLogin()
  const { phoneNumber } = user
  const formattedPhoneNumber = useMemo(() => formatPhoneNumber(phoneNumber), [phoneNumber])

  const handleVerification = async verificationCode => {
    setLoading(true)
    setErrorMessage(null)
    try {
      analytics.segmentTrack('Anonymous Clicked Validate Verification Code', {
        phoneNumber,
      })
      const { chatToken, completedSignup, id, jwt } = await apiManager.authPhone(
        phoneNumber,
        verificationCode
      )

      if (completedSignup) {
        await login({ chatToken, jwt, userId: id })
      }

      setVerified(true)
      setLoading(false)

      setTimeout(
        () =>
          onSubmit({
            completedSignup,
            jwt,
            phoneNumber,
          }),
        VERIFICATION_DELAY
      )
    } catch (err) {
      setLoading(false)
      setErrorMessage('Invalid verification code, please try again!')
    }
  }

  const focused = errorMessage || autoFocus
  return (
    <Form noValidate onSubmit={() => {}}>
      <CloseButton onClose={onClose} />
      <div className="w-full">
        <ProgressBar progress={progressPercent} />
        <Label
          className={classnames({
            'text-center': verified,
          })}
        >
          {verified ? "You're verified" : 'CODE'}
        </Label>
        {verified ? (
          <div className="w-full flex flex-col items-center">
            <div className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-solid border-monaLisaPink">
              <AnimatedCheckIcon className="w-4 h-4 text-monaLisaPink" />
            </div>
          </div>
        ) : (
          <ReactCodeInput
            className="rounded-xl bg-transparent"
            key={focused}
            type="number"
            placeholder={['-', '-', '-', '-', '-', '-']}
            required
            autoFocus={!!focused}
            loading={loading}
            disabled={loading}
            onComplete={handleVerification}
            fieldWidth="16.666%"
          />
        )}
        {errorMessage && <InputErrorMessage className="mt-2">{errorMessage}</InputErrorMessage>}
        <div className="flex flex-col items-center">
          <Description className="mb-10 w-52 mr-0.5">Sent to {formattedPhoneNumber}.</Description>
          <Action
            onClick={() => {
              analytics.segmentTrack('Anonymous Clicked Resend Verification Code', {
                phoneNumber,
              })
              verify(phoneNumber)
            }}
            className="mt-2"
          >
            Not working? <span className="underline text-sm">Resend code</span>
          </Action>
        </div>
      </div>
    </Form>
  )
}

VerificationModalCodeValidation.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  autoFocus: PropTypes.bool,
}

VerificationModalCodeValidation.defaultProps = {
  autoFocus: false,
}

export default VerificationModalCodeValidation
